import { useMutation } from '@apollo/client';
import { HostAddNoteToGuestDocument } from '@website/types/graphqlOperations';
import { useCallback, useState } from 'react';
import { Button, Form, FormControl, FormGroup, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

type Props = {
  spaceID: string;
  note?: string;
  variant?: 'link' | 'button';
};

export default function HostAddGuestNote({
  spaceID,
  note,
  variant = 'button',
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const [addNote, { loading }] = useMutation(HostAddNoteToGuestDocument);

  const {
    handleSubmit,
    register,
    formState: { isValid },
  } = useForm({
    values: {
      note,
    },
  });

  const submit = useCallback(
    async (values: { note?: string }) => {
      try {
        await addNote({
          variables: {
            spaceID,
            note: values.note === '' ? undefined : values.note,
          },
        });
        setIsOpen(false);
      } catch (e) {
        console.error(e);
        alert('Failed to add note');
      }
    },
    [addNote, spaceID],
  );

  return (
    <>
      {variant === 'button' ? (
        <Button variant={variant} size="sm" onClick={() => setIsOpen(true)}>
          {note ? 'Edit Note' : 'Add Note'}
        </Button>
      ) : (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <a
          className="text-primary"
          role="button"
          target="_blank"
          onClick={() => setIsOpen(true)}
        >
          {note ? 'Edit Note' : 'Add Note'}
        </a>
      )}
      <Modal show={isOpen} size="lg" onHide={() => setIsOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Note</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit(submit)}>
          <Modal.Body>
            <FormGroup controlId="formBasicEmail">
              <Form.Label>Reply</Form.Label>
              <FormControl
                as="textarea"
                placeholder="Add Note..."
                name="note"
                rows={6}
                {...register('note')}
              />
              <Form.Text className="text-muted">
                You can remove this note by deleting all text.
              </Form.Text>
            </FormGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button
              disabled={!isValid || loading}
              type="submit"
              variant="primary"
            >
              {loading ? <>Saving</> : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
